import { useShallow } from 'zustand/react/shallow';
import { useChatStore } from '../stores/chatStore';
import { useCallback, useEffect, useMemo } from 'react';

export interface ActiveAgentMention {
    atIndex: number;
    endIndex: number;
    query: string;
}

/**
 * Finds an agent mention token immediately before the caret.
 * Mentions must start at the beginning of the input or after whitespace, so
 * embedded @ characters such as those in email addresses are ignored.
 */
export function getActiveAgentMention(
    inputValue: string,
    caretPosition: number = inputValue.length,
): ActiveAgentMention | null {
    const safeCaretPosition = Math.max(0, Math.min(caretPosition, inputValue.length));
    const textBeforeCaret = inputValue.slice(0, safeCaretPosition);
    const match = /(?:^|\s)@([^\s@]*)$/.exec(textBeforeCaret);

    if (!match) {
        return null;
    }

    return {
        atIndex: safeCaretPosition - match[1].length - 1,
        endIndex: safeCaretPosition,
        query: match[1],
    };
}

/**
 * Custom hook for listening to the input and if an @ is detected render the filtered agent list
 * @returns The filter string and function to remove the @ input
 */
export function useAgentSelector(conversationID: string, caretPosition?: number | null) {
    const {
        setInputValue,
        getInputValue,
        openAtAgentsModal,
        closeAtAgentsModal,
        isAtAgentsModalOpen
    } = useChatStore(useShallow((state) => ({
        setInputValue: state.setInputValue,
        getInputValue: state.getInputValue,
        openAtAgentsModal: state.openAtAgentsModal,
        closeAtAgentsModal: state.closeAtAgentsModal,
        isAtAgentsModalOpen: state.isAtAgentsModalOpen,
    })));

    const inputValue = getInputValue(conversationID);
    const activeMention = useMemo(
        () => getActiveAgentMention(inputValue, caretPosition ?? inputValue.length),
        [caretPosition, inputValue],
    );
    const mentionFilter = activeMention?.query ?? '';

    const removeAtInput = useCallback(() => {
        if (!activeMention) {
            return;
        }

        const newValue = inputValue.slice(0, activeMention.atIndex)
            + inputValue.slice(activeMention.endIndex);
        setInputValue(conversationID, newValue);
    }, [activeMention, conversationID, inputValue, setInputValue]);

    useEffect(() => {
        if (activeMention && !isAtAgentsModalOpen) {
            openAtAgentsModal();
        } else if (!activeMention && isAtAgentsModalOpen) {
            closeAtAgentsModal();
        }
    }, [activeMention, isAtAgentsModalOpen, openAtAgentsModal, closeAtAgentsModal]);

    return {mentionFilter, removeAtInput};
}
